/**
 * 夥伴詳情 — Webhook 分頁（AC-G-11 / G13）。
 *
 * 三個動作的差別容易混淆，UI 上刻意分開並各自說明：
 *   設定／更換 URL → 連 secret 一起換（後端 setWebhook 行為即如此）
 *   輪替 secret    → 只換 secret，URL 不動
 *   解除暫停       → 連續投遞失敗被自動暫停後的復原
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { Field, ModalShell, errorTextStyle, inputStyle, useEscapeToClose } from './Modal';
import { Pill } from './Pill';
import { SecretHandoffModal } from './SecretHandoff';
import {
  resumePartnerWebhook,
  rotatePartnerWebhookSecret,
  setPartnerWebhook,
  testPartnerWebhook,
  type Partner,
  type WebhookDispatchResult,
  type WebhookSecretResult,
} from '../lib/api-client';
import { formatDateTime } from '../lib/partner-format';

const DISPATCH_SKIP_REASON: Record<string, string> = {
  no_webhook_url: '尚未設定 Webhook URL',
  webhook_paused: 'Webhook 已被自動暫停，請先解除',
};

export function PartnerWebhookTab({ partner }: { partner: Partner }): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [issued, setIssued] = useState<WebhookSecretResult | null>(null);
  const [dispatch, setDispatch] = useState<WebhookDispatchResult | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['partners.detail', partner.id] });
    void queryClient.invalidateQueries({ queryKey: ['partners.list'] });
  };

  const rotate = useMutation({
    mutationFn: () => rotatePartnerWebhookSecret(partner.id),
    onSuccess: (r) => {
      invalidate();
      setIssued(r);
    },
  });

  const test = useMutation({
    mutationFn: () => testPartnerWebhook(partner.id),
    onSuccess: setDispatch,
  });

  const resume = useMutation({
    mutationFn: () => resumePartnerWebhook(partner.id),
    onSuccess: invalidate,
  });

  const configured = Boolean(partner.webhook_url);

  return (
    <div>
      <div className="table-card" style={{ marginBottom: 20 }}>
        <table>
          <tbody>
            <tr>
              <td style={{ width: 160, color: '#6b7280', fontSize: 13 }}>推送位址</td>
              <td>
                {configured ? (
                  <code style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                    {partner.webhook_url}
                  </code>
                ) : (
                  <span style={{ color: '#9ca3af' }}>尚未設定 —— 此夥伴不會收到任何事件推送</span>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ color: '#6b7280', fontSize: 13 }}>投遞狀態</td>
              <td>
                {!configured ? (
                  <span style={{ color: '#9ca3af' }}>—</span>
                ) : partner.webhook_paused ? (
                  <>
                    <Pill tone="warning">已自動暫停</Pill>
                    {partner.webhook_paused_reason && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        {partner.webhook_paused_reason}
                      </div>
                    )}
                  </>
                ) : (
                  <Pill tone="success">正常</Pill>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ color: '#6b7280', fontSize: 13 }}>Secret 上次輪替</td>
              <td style={{ fontSize: 13 }}>
                {partner.webhook_secret_rotated_at
                  ? formatDateTime(partner.webhook_secret_rotated_at)
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Button variant="primary" onClick={() => setEditing(true)}>
          {configured ? '更換推送位址' : '設定推送位址'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => rotate.mutate()}
          disabled={!configured || rotate.isPending}
        >
          {rotate.isPending ? '輪替中…' : '輪替 Secret'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => test.mutate()}
          disabled={!configured || test.isPending}
        >
          {test.isPending ? '投遞中…' : '送出測試事件'}
        </Button>
        {partner.webhook_paused && (
          <Button variant="danger" onClick={() => resume.mutate()} disabled={resume.isPending}>
            {resume.isPending ? '解除中…' : '解除暫停'}
          </Button>
        )}
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
        更換位址會<strong>連同 secret 一起換發</strong>；只想換 secret 請用「輪替 Secret」。
        兩者都會保留舊 secret 一段重疊期並雙簽，夥伴可在期限內從容切換。
      </p>

      {[rotate, test, resume].map((m, i) =>
        m.isError ? (
          <div key={i} className="error-banner" style={{ marginBottom: 8 }}>
            {(m.error as Error).message}
          </div>
        ) : null,
      )}

      {dispatch && (
        <div
          className={dispatch.skipped || dispatch.delivered === false ? 'error-banner' : undefined}
          style={
            dispatch.skipped || dispatch.delivered === false
              ? { marginBottom: 8 }
              : {
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: 6,
                  padding: '10px 14px',
                  fontSize: 13,
                  marginBottom: 8,
                }
          }
        >
          {dispatch.skipped
            ? `未投遞：${DISPATCH_SKIP_REASON[dispatch.reason ?? ''] ?? dispatch.reason}`
            : dispatch.delivered
              ? `投遞成功（event_id ${dispatch.event_id}）—— 對方端點已回 2xx`
              : `已送出但對方未回 2xx（event_id ${dispatch.event_id}）—— 系統會依重送策略再試`}
        </div>
      )}

      {editing && (
        <SetWebhookModal
          partner={partner}
          onClose={() => setEditing(false)}
          onDone={(r) => {
            invalidate();
            setEditing(false);
            setIssued(r);
          }}
        />
      )}

      {issued && (
        <SecretHandoffModal
          title="Webhook Secret 已換發"
          subtitle={
            <code style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              {issued.webhook_url}
            </code>
          }
          warning={
            <>
              <strong>此 secret 只會顯示這一次。</strong>
              夥伴需以它驗證推送簽章（HMAC-SHA256）。
              {issued.previous_secret_valid_until ? (
                <>
                  {' '}
                  舊 secret 於 <strong>
                    {formatDateTime(issued.previous_secret_valid_until)}
                  </strong>{' '}
                  前仍有效，期間兩把都驗得過，請在此之前完成切換。
                </>
              ) : (
                ' 此夥伴先前沒有 secret，無重疊期。'
              )}
            </>
          }
          secretLabel="Webhook Secret"
          secret={issued.webhook_secret}
          onAcknowledge={() => setIssued(null)}
        />
      )}
    </div>
  );
}

function SetWebhookModal({
  partner,
  onClose,
  onDone,
}: {
  partner: Partner;
  onClose: () => void;
  onDone: (r: WebhookSecretResult) => void;
}): JSX.Element {
  const [url, setUrl] = useState(partner.webhook_url ?? '');
  const mutation = useMutation({
    mutationFn: () => setPartnerWebhook(partner.id, url.trim()),
    onSuccess: onDone,
  });

  useEscapeToClose(onClose, !mutation.isPending);

  const trimmed = url.trim();
  // 後端用 IsUrl 驗；這裡只擋最常見的漏 scheme，讓錯誤在送出前就看得到
  const looksInvalid = trimmed.length > 0 && !/^https?:\/\/.+/.test(trimmed);
  const canSubmit = trimmed.length > 0 && !looksInvalid && !mutation.isPending;

  return (
    <ModalShell onBackdropClick={mutation.isPending ? undefined : onClose} width={560}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>
        {partner.webhook_url ? '更換推送位址' : '設定推送位址'}
      </h2>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>
        訂票成功、退票完成、班次異動時，系統會以 POST 推送至此位址。
        {partner.webhook_url && '送出後會一併換發新 secret。'}
      </p>

      <Field label="Webhook URL" hint="需為 https（正式環境）；由夥伴提供">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://partner.example.com/hooks/boat"
          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13 }}
          autoFocus
        />
        {looksInvalid && <div style={errorTextStyle}>需以 http:// 或 https:// 開頭</div>}
      </Field>

      {mutation.isError && (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          {(mutation.error as Error).message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
          取消
        </Button>
        <Button variant="primary" onClick={() => mutation.mutate()} disabled={!canSubmit}>
          {mutation.isPending ? '儲存中…' : '儲存並換發 Secret'}
        </Button>
      </div>
    </ModalShell>
  );
}
